import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { MobileCenteredLayout } from '@/components/MobileCenteredLayout/MobileCenteredLayout';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useCurrentUrl } from '@/hooks/useCurrentUrl';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MOBILE_NAMESPACE } from '@/i18n';
import { trackMobileEvent } from '@/helpers/analytics';
import { MOBILE_FUNNEL_ENTRY } from '@/helpers/onboarding-routes';
import { CopyLinkRow } from '@/Pages/Mobile/components/CopyLinkRow';
import { MobileValidationFooter } from '@/Pages/Mobile/components/MobileValidationFooter';
import { MobileValidationHeader } from '@/Pages/Mobile/components/MobileValidationHeader';
import { QRCodeDisplay } from '@/Pages/Mobile/components/QRCodeDisplay';
import { ScanInstructions } from '@/Pages/Mobile/components/ScanInstructions';
import '@/Pages/Mobile/MobilePage.sass';

export function MobilePage(): JSX.Element {
  const isMobile = useIsMobile();
  const location = useLocation();
  const currentUrl = useCurrentUrl();
  const { status, copy } = useCopyToClipboard();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { t } = useTranslation(MOBILE_NAMESPACE);

  useEffect(() => {
    void trackMobileEvent({ name: 'mobile_page_view' });
  }, []);

  if (isMobile) {
    const sessionId = new URLSearchParams(location.search).get('sessionId');
    if (typeof sessionId === 'string' && sessionId !== '') {
      return (
        <Navigate to={MOBILE_FUNNEL_ENTRY} replace state={{ sessionId }} />
      );
    }

    return <Navigate to={MOBILE_FUNNEL_ENTRY} replace />;
  }

  return (
    <MobileCenteredLayout>
      <Box
        component="main"
        className={
          prefersReducedMotion
            ? 'mobile-validation-page mobile-validation-page--reduced-motion'
            : 'mobile-validation-page'
        }
        data-testid="mobile-validation-page"
        data-motion={prefersReducedMotion ? 'reduced' : 'animated'}
      >
        <MobileValidationHeader />
        <QRCodeDisplay
          value={currentUrl}
          caption={t('qr.caption')}
          title={t('qr.imageTitle')}
        />
        <CopyLinkRow url={currentUrl} onCopy={copy} status={status} />
        <ScanInstructions />
        <MobileValidationFooter />
      </Box>
    </MobileCenteredLayout>
  );
}
